import React, { Suspense, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LOCAL_STORAGE_TAB_ID, SESSION_STORAGE_TAB_ID } from 'lib/constants';
import { DESTINATION_GROUP, NAVIGATION_TRIGGER_TYPE } from 'lib/SearchlightDispatcher';
import { lazyWithRetry } from 'lib/utilityFunctions';
import { reportMultipleSessions } from 'a/actions/searchlightActions';
import { getAllGamesState } from 'a/actions/getAllGamesState';
import { goToHubScreen } from 'a/hub/actions/metaGameActions';
import { closeMultipleSessionsModal } from 'a/hub/actions/modalSystemActions';
import { ASSESSMENT_IDS } from 'a/hub/data/AssessmentIds';
import { getIsDeepLink } from 'a/hub/selectors';
import { getSelectedActivity, getSendingSearchlight } from 'a/hub/selectors/sessionSelectors';

const ModalComponent = lazyWithRetry(() => import('a/hub/components/MultipleSessionsOpenModal'));

const MultipleSessionsOpenModal = () => {
  const dispatch = useDispatch();
  const gameId = useSelector(getSelectedActivity);
  const sendingSearchlight = useSelector(getSendingSearchlight);
  const isAssessment = ASSESSMENT_IDS.has(gameId);
  const isDeepLink = useSelector(getIsDeepLink);
  const [isRefreshButtonActive, setIsRefreshButtonActive] = useState(true);

  const sendingSearchlightRef = useRef(sendingSearchlight);
  const action = 'refresh_button_selected';

  useEffect(() => {
    const refreshSession = async () => {
      if (!sendingSearchlight && sendingSearchlightRef.current?.details?.action === action) {
        // If not currently loading data, taking an assessment, or in deep link mode,
        // update stored timestamps after selecting the session (tab) to refresh,
        // return to Home screen (Bookerton/Workshop) and skip reloading the page.
        if (gameId !== 'loading' && !isAssessment && !isDeepLink) {
          const newTabId = new Date().toISOString();
          sessionStorage.setItem(SESSION_STORAGE_TAB_ID, newTabId);
          localStorage.setItem(LOCAL_STORAGE_TAB_ID, newTabId);

          const navigationParams = {
            destinationGroup: DESTINATION_GROUP.MAP,
            origin: gameId,
            trigger: 'multipleSessionsModal',
            triggerType: NAVIGATION_TRIGGER_TYPE.MANUAL,
          };
          await dispatch(getAllGamesState());
          dispatch(goToHubScreen({ screenName: 'Map', navigationParams }));
          dispatch(closeMultipleSessionsModal());
        } else {
          // Edge case for loading/product selection screens, assessments, and deep links:
          // Reload page to refresh additional data and/or avoid metagame redirect
          window.location.reload();
        }
      }
    };

    refreshSession();
    sendingSearchlightRef.current = sendingSearchlight;
  }, [sendingSearchlight]);

  const handleSessionRefresh = () => {
    setIsRefreshButtonActive(false);
    const localStorageId = localStorage.getItem(LOCAL_STORAGE_TAB_ID) ?? 'N/A';
    const sessionStorageId = sessionStorage.getItem(SESSION_STORAGE_TAB_ID) ?? 'N/A';
    dispatch(reportMultipleSessions(gameId, { action, localStorageId, sessionStorageId }));
  };

  return (
    <Suspense fallback={null}>
      <ModalComponent
        isRefreshButtonActive={isRefreshButtonActive}
        onRefresh={handleSessionRefresh}
      />
    </Suspense>
  );
};

export default MultipleSessionsOpenModal;
